import Products from 'merchandise-lib/constants/Product';
import { useMemo } from 'react';
import { calculateHighestProductLevel, useHighestProductLevelFromInventory } from '../../../hooks/inventory/useHighestProductLevel';
import { useIsDowngrade } from '../../../hooks/inventory/useProductInventory';
import { useHasSkuCategoryNameInAnyInventory } from '../../../hooks/inventory/useProductInventoryLookup';
import { usePerMonthPriceForTerm } from '../../../hooks/pricing/useGrowthPurchaseOptions';
import { useProductDetails } from '../../../hooks/product/useProductDetails';
import { isValidProductLevel, isValidSkuCategoryName } from '../../../utils/typeguards';

/**
 * Hook to retrieve the number of included seats for different seat types from product details.
 *
 * @param params - Configuration object
 * @param params.productApiName - The API name of the product to get seat information for
 * @returns Object containing:
 *   - includedCoreSeats: Number of included core seats, or null if not available
 *   - includedSalesSeats: Number of included sales seats, or null if not available
 *   - includedServiceSeats: Number of included service seats, or null if not available
 *   - includedCommerceSeats: Number of included commerce seats, or null if not available
 *   - loading: Boolean indicating if product details are still loading
 */
export const useIncludedSeats = ({
  productApiName
}) => {
  var _seatsMap$get, _seatsMap$get2, _seatsMap$get3, _seatsMap$get4;
  const {
    productDetails,
    loading
  } = useProductDetails({
    productApiName
  });
  const packagedQuantities = productDetails === null || productDetails === void 0 ? void 0 : productDetails.packagedQuantities;
  const seatsMap = useMemo(() => {
    if (!packagedQuantities) return new Map();
    return new Map(packagedQuantities.map(packagedQuantity => [packagedQuantity.name, packagedQuantity.quantity]));
  }, [packagedQuantities]);
  const includedCoreSeats = (_seatsMap$get = seatsMap.get('CORE_SEAT')) !== null && _seatsMap$get !== void 0 ? _seatsMap$get : null;
  const includedSalesSeats = (_seatsMap$get2 = seatsMap.get('SALES_SEAT')) !== null && _seatsMap$get2 !== void 0 ? _seatsMap$get2 : null;
  const includedServiceSeats = (_seatsMap$get3 = seatsMap.get('SERVICE_SEAT')) !== null && _seatsMap$get3 !== void 0 ? _seatsMap$get3 : null;
  const includedCommerceSeats = (_seatsMap$get4 = seatsMap.get('COMMERCE_SEAT')) !== null && _seatsMap$get4 !== void 0 ? _seatsMap$get4 : null;
  return {
    includedCoreSeats,
    includedSalesSeats,
    includedServiceSeats,
    includedCommerceSeats,
    loading
  };
};

/**
 * Hook that determines whether to display pricing in "/mo/seat" format based on product characteristics
 * and customer ownership status. The logic considers product quantifiability, customer tier,
 * and ownership of the product's SKU category.
 *
 * @param params - Configuration object
 * @param params.productApiName - The API name of the product to evaluate
 * @returns Object containing:
 *   - showPricePerMonthPerSeat: Boolean indicating whether to show price in "/mo/seat" format
 *   - loading: Boolean indicating if any data is still loading
 *   - error: Any error that occurred during data fetching, or null
 */
export const useShowPricePerMonthPerSeat = ({
  productApiName
}) => {
  const {
    productDetails,
    loading: productDetailsLoading,
    error: productDetailsError
  } = useProductDetails({
    productApiName
  });
  const isQuantifiableProduct = productDetails === null || productDetails === void 0 ? void 0 : productDetails.quantifiable;
  const productLevel = productDetails === null || productDetails === void 0 ? void 0 : productDetails.productLevel;
  const skuCategoryName = productDetails === null || productDetails === void 0 ? void 0 : productDetails.skuCategoryName;
  const {
    hasSkuCategoryNameInAnyInventory: ownsSkuCategory,
    loading: ownershipLoading,
    error: ownershipError
  } = useHasSkuCategoryNameInAnyInventory({
    skuCategoryName
  });
  const {
    highestProductLevel,
    loading: inventoryLoading,
    error: inventoryError
  } = useHighestProductLevelFromInventory();

  // Aggregate loading and error states
  const loading = productDetailsLoading || ownershipLoading || inventoryLoading;
  const error = productDetailsError || ownershipError || inventoryError;

  // Return early with loading state if still loading
  if (loading) {
    return {
      showPricePerMonthPerSeat: false,
      loading,
      error
    };
  }

  // Return false if essential product information is missing
  if (!productLevel || !skuCategoryName) {
    return {
      showPricePerMonthPerSeat: false,
      loading,
      error
    };
  }
  if (isQuantifiableProduct) {
    return {
      showPricePerMonthPerSeat: true,
      loading,
      error
    };
  }
  const isFreeOrStarterCustomer = highestProductLevel === 'FREE' || highestProductLevel === 'STARTER';
  if (productLevel === 'STARTER') {
    // If the highest owned tier is free or starter, show the price in "/mo/seat" format on ALL Starter cards
    if (isFreeOrStarterCustomer) {
      return {
        showPricePerMonthPerSeat: true,
        loading,
        error
      };
    }

    // If looking at merch card of a hub OWNED, show price in "/mo/seat" format on Starter cards
    if (ownsSkuCategory) {
      return {
        showPricePerMonthPerSeat: true,
        loading,
        error
      };
    }
    return {
      showPricePerMonthPerSeat: false,
      loading,
      error
    };
  }
  return {
    showPricePerMonthPerSeat: false,
    loading,
    error
  };
};

/**
 * Hook that determines whether to show "Includes Y seats" text on product cards.
 * The display logic varies based on product level, quantifiability, and customer ownership status.
 * Shows for non-quantifiable Pro/Enterprise products and Starter products under specific conditions.
 *
 * @param params - Configuration object
 * @param params.productApiName - The API name of the product to evaluate
 * @returns Object containing:
 *   - showIncludedSeats: Boolean indicating whether to display "Includes Y seats" text
 *   - loading: Boolean indicating if any data is still loading
 *   - error: Any error that occurred during data fetching, or null
 */
export const useShowIncludedSeats = ({
  productApiName
}) => {
  const {
    productDetails,
    loading: productDetailsLoading,
    error: productDetailsError
  } = useProductDetails({
    productApiName
  });
  const isQuantifiableProduct = productDetails === null || productDetails === void 0 ? void 0 : productDetails.quantifiable;
  const productLevel = productDetails === null || productDetails === void 0 ? void 0 : productDetails.productLevel;
  const skuCategoryName = productDetails === null || productDetails === void 0 ? void 0 : productDetails.skuCategoryName;
  const {
    hasSkuCategoryNameInAnyInventory: ownsSkuCategory,
    loading: ownershipLoading,
    error: ownershipError
  } = useHasSkuCategoryNameInAnyInventory({
    skuCategoryName
  });
  const isProOrEnterpriseProduct = productLevel === 'PROFESSIONAL' || productLevel === 'ENTERPRISE';
  const isNonQuantifiableProOrEnterpriseProduct = isProOrEnterpriseProduct && !isQuantifiableProduct;
  const {
    showPricePerMonthPerSeat
  } = useShowPricePerMonthPerSeat({
    productApiName
  });

  // Aggregate loading and error states
  const loading = productDetailsLoading || ownershipLoading;
  const error = productDetailsError || ownershipError;

  // Return early with loading state if still loading
  if (loading) {
    return {
      showIncludedSeats: false,
      loading,
      error
    };
  }

  // Return false if essential product information is missing
  if (!productLevel || !skuCategoryName) {
    return {
      showIncludedSeats: false,
      loading,
      error
    };
  }

  // Show "Includes Y seats" on ALL Non-quantifiable hubs' Pro/Ent card
  if (isNonQuantifiableProOrEnterpriseProduct) {
    return {
      showIncludedSeats: true,
      loading,
      error
    };
  }

  // Show "Includes Y seats" on Starter cards only if they DO NOT OWN productLine [and NOT in "/mo/seat" format]
  const productLineOwnershipIncludedSeatsRule = productLevel === 'STARTER' && !ownsSkuCategory && !showPricePerMonthPerSeat;
  return {
    showIncludedSeats: productLineOwnershipIncludedSeatsRule,
    loading,
    error
  };
};
export const CoreSeatProductApiNameByProductLevel = {
  FREE: Products.CORE_SEATS_STARTER,
  STARTER: Products.CORE_SEATS_STARTER,
  PROFESSIONAL: Products.CORE_SEATS_PROFESSIONAL,
  ENTERPRISE: Products.CORE_SEATS_ENTERPRISE
};

/**
 * Hook that calculates the price for additional core seats based on the highest product level
 * between what the customer owns and the current product being viewed. Uses the appropriate
 * core seat product API name for the determined product level.
 *
 * @param params - Configuration object
 * @param params.productApiName - The API name of the product being viewed
 * @param params.activeTerm - The billing term (ANNUAL or MONTHLY) for pricing calculation
 * @returns Object containing:
 *   - additionalCoreSeatsPrice: Price for additional core seats, or null if not available
 *   - loading: Boolean indicating if pricing data is still loading
 *   - error: Any error that occurred during price fetching, or null
 */
export const useAdditionalCoreSeatPrice = ({
  productApiName,
  activeTerm
}) => {
  // highest product level owned or merch card's product level, whichever higher
  const {
    highestProductLevel: highestProductLevelOwned
  } = useHighestProductLevelFromInventory();
  const {
    productDetails
  } = useProductDetails({
    productApiName
  });
  const productLevel = productDetails === null || productDetails === void 0 ? void 0 : productDetails.productLevel;
  const coreSeatProductLevelRequired = calculateHighestProductLevel([highestProductLevelOwned, productLevel]);
  const coreSeatProductApiName = CoreSeatProductApiNameByProductLevel[coreSeatProductLevelRequired];
  const {
    price,
    loading,
    error
  } = usePerMonthPriceForTerm({
    productApiName: coreSeatProductApiName,
    term: activeTerm
  });
  const additionalCoreSeatsPrice = price || null;
  return {
    additionalCoreSeatsPrice,
    loading,
    error
  };
};

/**
 * Hook that determines whether to show "Additional Seats start at $X/mo" subtext on product cards.
 * Shows for non-quantifiable hub cards that are not downgrades, not in "/mo/seat" format,
 * not free products, and have a valid additional core seats price.
 *
 * @param params - Configuration object
 * @param params.productApiName - The API name of the product to evaluate
 * @param params.activeTerm - The billing term (ANNUAL or MONTHLY) for pricing calculation
 * @returns Object containing:
 *   - showAdditionalSeatsPriceSubtext: Boolean indicating whether to show the additional seats price subtext
 *   - loading: Boolean indicating if any data is still loading
 *   - error: Any error that occurred during data fetching, or null
 */
export const useShowAdditionalSeatsPriceSubtext = ({
  productApiName,
  activeTerm
}) => {
  const {
    productDetails,
    loading: productDetailsLoading,
    error: productDetailsError
  } = useProductDetails({
    productApiName
  });
  const productLevel = productDetails === null || productDetails === void 0 ? void 0 : productDetails.productLevel;
  const {
    isDowngrade,
    loading: isDowngradeLoading,
    error: isDowngradeError
  } = useIsDowngrade({
    productLevel: isValidProductLevel(productLevel) ? productLevel : undefined,
    skuCategoryName: isValidSkuCategoryName(productDetails === null || productDetails === void 0 ? void 0 : productDetails.skuCategoryName) ? productDetails === null || productDetails === void 0 ? void 0 : productDetails.skuCategoryName : undefined
  });
  const isFreeProduct = productLevel === 'FREE';
  const {
    showPricePerMonthPerSeat,
    loading: showPricePerMonthPerSeatLoading,
    error: showPricePerMonthPerSeatError
  } = useShowPricePerMonthPerSeat({
    productApiName
  });
  const {
    additionalCoreSeatsPrice,
    loading: additionalCoreSeatsPriceLoading,
    error: additionalCoreSeatsPriceError
  } = useAdditionalCoreSeatPrice({
    productApiName,
    activeTerm
  });
  const isValidProduct = isValidProductLevel(productLevel) && isValidSkuCategoryName(productDetails === null || productDetails === void 0 ? void 0 : productDetails.skuCategoryName);
  const showAdditionalSeatsPriceSubtext = additionalCoreSeatsPrice && additionalCoreSeatsPrice > 0 && !isDowngrade && !showPricePerMonthPerSeat && !isFreeProduct && isValidProduct;
  return {
    showAdditionalSeatsPriceSubtext: !!showAdditionalSeatsPriceSubtext,
    loading: productDetailsLoading || showPricePerMonthPerSeatLoading || additionalCoreSeatsPriceLoading || isDowngradeLoading,
    error: productDetailsError || showPricePerMonthPerSeatError || additionalCoreSeatsPriceError || isDowngradeError
  };
};